import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { CATALOG_KEY, LOCATIONS_KEY, useLocations, type Location } from './ItemForm';
import { useUnsavedGuard, validForm } from '../ui/unsaved';

function Problem({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="alert" role="alert">
      {error.status === 409 ? 'Ktoś już zmienił tę lokalizację. Odśwież stronę.' : (fieldIssues(error)[0]?.message ?? error.message)}
    </p>
  );
}

function LocationRow({ location, canEdit, canDelete }: { location: Location; canEdit: boolean; canDelete: boolean }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(location.name);
  const refresh = () => queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
  const rename = useMutation({ mutationFn: () => api(`/api/catalog/locations/${location.id}`, { method: 'PATCH', body: { version: location.version, name: name.trim() } }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => api(`/api/catalog/locations/${location.id}?version=${location.version}`, { method: 'DELETE' }), onSuccess: refresh });
  // owner 2026-10-23 21:14: leaving with unsaved changes asks first
  const guardRef = useUnsavedGuard({
    dirty: canEdit && name.trim() !== location.name,
    save: async (el) => {
      if (!validForm(el)) return false;
      await rename.mutateAsync();
      return true;
    },
  });
  return (
    <li className="row">
      <form
        ref={guardRef}
        className="row-main inline-form"
        aria-label={`Lokalizacja: ${location.name}`}
        onSubmit={(e) => {
          e.preventDefault();
          rename.mutate();
        }}
      >
        <label className="field w-md">
          <span className="visually-hidden">Nazwa lokalizacji</span>
          <input value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} required autoComplete="off" />
        </label>
        {canEdit && name.trim() !== location.name && (
          <button className="btn small" type="submit" disabled={rename.isPending}>
            Zmień nazwę
          </button>
        )}
        {canDelete && (
          <button className="btn ghost small" type="button" aria-label={`Usuń lokalizację: ${location.name}`} disabled={remove.isPending} onClick={() => remove.mutate()}>
            Usuń
          </button>
        )}
        <Problem error={rename.error ?? remove.error} />
      </form>
    </li>
  );
}

/** The locations dictionary ("Magazyn" + own), behind the catalog's gear (remark 3/25, rule 9 of docs/wyglad.md). */
export function LocationsSettings({ canCreate, canEdit, canDelete }: { canCreate: boolean; canEdit: boolean; canDelete: boolean }) {
  const queryClient = useQueryClient();
  const locations = useLocations().data?.locations ?? [];
  const [name, setName] = useState('');
  const add = useMutation({
    mutationFn: () => api('/api/catalog/locations', { method: 'POST', body: { name: name.trim() } }),
    onSuccess: async () => {
      setName('');
      await queryClient.invalidateQueries({ queryKey: LOCATIONS_KEY });
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    add.mutate();
  }
  const addRef = useUnsavedGuard({
    dirty: name.trim() !== '',
    save: async () => {
      await add.mutateAsync();
      return true;
    },
  });
  return (
    <section className="panel settings-panel" aria-label="Lokalizacje" data-feature-id="catalog.locations">
      <h2>Lokalizacje</h2>
      <p className="hint">Gdzie zwykle stoi sprzęt. Pierwsza na liście jest domyślna dla nowych pozycji. Nową lokalizację można też wpisać w „Gdzie teraz” przy pozycji.</p>
      <ul className="list">
        {locations.map((l) => (
          <LocationRow key={`${l.id}-${l.version}`} location={l} canEdit={canEdit} canDelete={canDelete} />
        ))}
      </ul>
      {canCreate && (
        <form ref={addRef} className="inline-form" aria-label="Nowa lokalizacja" onSubmit={submit}>
          <label className="field w-md">
            <span>Nazwa nowej lokalizacji</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" placeholder="np. Magazyn Gdynia" />
          </label>
          <button className="btn" type="submit" disabled={add.isPending}>
            Dodaj lokalizację
          </button>
          <Problem error={add.error} />
        </form>
      )}
    </section>
  );
}
